import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY.current;

      setIsScrolled(currentScrollY > 10);

      if (isOpen || currentScrollY <= 24) {
        setIsVisible(true);
      } else if (Math.abs(scrollDelta) >= 8) {
        setIsVisible(scrollDelta < 0);
      }

      lastScrollY.current = currentScrollY;
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  useEffect(() => {
    setIsVisible(true);
    lastScrollY.current = window.scrollY;
  }, [location.pathname, isOpen]);

  useEffect(() => {
    const root = document.documentElement;

    const updateHeaderHeight = () => {
      const height = headerRef.current?.offsetHeight ?? 0;
      root.style.setProperty('--header-height', `${height}px`);
      root.style.setProperty('--header-visible-height', isVisible ? `${height}px` : '0px');
    };

    updateHeaderHeight();

    const resizeObserver = typeof ResizeObserver !== 'undefined' && headerRef.current
      ? new ResizeObserver(updateHeaderHeight)
      : null;

    if (resizeObserver && headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [isVisible, isScrolled, isOpen, location.pathname]);

  // Define which pages should initially have a transparent header
  const isTransparentHeaderPage = ['/', '/recursos'].includes(location.pathname);
  
  // Specific check for resources page (dark background)
  const isResourcesPage = location.pathname === '/recursos';

  // Helper to determine text colors based on state
  const getTextColor = (baseColor: string, scrolledColor: string, darkBgColor: string) => {
    // If we have scrolled, we are on white glass background -> use dark text (scrolledColor)
    if (isScrolled) return scrolledColor;
    
    // If we are on a page that is NOT transparent (like blog), use standard dark text
    if (!isTransparentHeaderPage) return scrolledColor;

    // We are at the top (transparent)
    // If on Resources page (dark bg), use White text
    if (isResourcesPage) return darkBgColor;
    
    // If on Home page (light bg), use base Color (which is Contessa dark)
    return baseColor;
  };

  const navLinkColorClass = getTextColor(
    'text-contessa-800 hover:text-contessa-600', // Home transparent
    'text-contessa-900',                         // Scrolled / Solid
    'text-white hover:text-contessa-100'         // Resources transparent (Dark BG)
  );

  const logoColorClass = getTextColor(
    'text-contessa-700', // Home transparent
    'text-contessa-700', // Scrolled / Solid
    'text-white'         // Resources transparent
  );

  const logoSpanColorClass = getTextColor(
    'text-contessa-500', // Home transparent
    'text-contessa-500', // Scrolled / Solid
    'text-contessa-200'  // Resources transparent
  );

  const menuButtonColorClass = getTextColor(
    'text-contessa-700',
    'text-contessa-700',
    'text-white'
  );

  // Optimized scroll function with debouncing
  const scrollToSection = (sectionId: string) => {
    setIsOpen(false);

    if (location.pathname !== '/') {
      // Use React Router navigation instead of window.location.href
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      // Debounced scroll to avoid excessive calls
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  // Handle scroll after navigation
  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 300);
      // Clear the state to prevent re-scrolling
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  return (
    <header
      ref={headerRef}
      className={`fixed w-full z-50 transform transition-all duration-500 ${isVisible ? 'translate-y-0' : '-translate-y-full'} ${isScrolled || !isTransparentHeaderPage
          ? 'bg-white/70 backdrop-blur-md shadow-sm border-b border-white/20 py-3 supports-[backdrop-filter]:bg-white/60'
          : 'bg-transparent py-8 lg:py-10'
        }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link
              to="/"
              onClick={() => scrollToSection('hero')}
              className={`${logoColorClass} font-serif font-bold text-lg mr-4 transition-colors duration-300`}
            >
              <h1 className="text-2xl font-serif font-bold">
                Los ciclos de <span className={`${logoSpanColorClass} transition-colors duration-300`}>Marha</span>
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {[
              ['presentacion', '¿Quién soy?'],
              ['servicios', 'Servicios'],
              ['planes', 'Planes']
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className={`text-sm font-medium transition-colors duration-300 ${navLinkColorClass}`}
              >
                {label}
              </button>
            ))}
            <Link
              to="/recursos"
              className={`text-sm font-medium transition-colors duration-300 ${navLinkColorClass}`}
              onClick={() => {
                setIsOpen(false);
                window.scrollTo({ top: 0 });
              }}
            >
              Recursos
            </Link>
            <Link
              to="/blog"
              className="bg-contessa-500 hover:bg-contessa-600 text-white px-5 py-2 rounded-full transition-colors duration-300 text-sm"
              onClick={() => {
                setIsOpen(false);
                window.scrollTo({ top: 0 });
              }}
            >
              Blog
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden ${menuButtonColorClass} transition-colors duration-300`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            {isOpen ? <X size={24} className="text-contessa-800" /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white/90 backdrop-blur-xl py-6 px-6 shadow-xl border-t border-white/20 absolute w-full left-0 top-full">
          <nav className="flex flex-col space-y-4">
            {[
              ['presentacion', '¿Quién soy?'],
              ['servicios', 'Servicios'],
              ['planes', 'Planes']
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className="text-contessa-800 hover:text-contessa-500 transition-colors py-2"
              >
                {label}
              </button>
            ))}
            <Link
              to="/recursos"
              className="text-contessa-800 hover:text-contessa-500 transition-colors py-2 mx-auto"
              onClick={() => {
                setIsOpen(false);
                window.scrollTo({ top: 0 });
              }}
            >
              Recursos
            </Link>
            <Link
              to="/blog"
              className="bg-contessa-500 hover:bg-contessa-600 text-white px-5 py-2 rounded-full transition-colors duration-300 self-center mt-2"
              onClick={() => {
                setIsOpen(false);
                window.scrollTo({ top: 0 });
              }}
            >
              Blog
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;